import { ContainerNode } from './container-node';
import { LeafNode } from './leaf-node';
import { TreeNode } from './tree-node';

export class Tree<TContainer, TLeaf> {
  constructor(
    public root: ContainerNode<TContainer, TLeaf>,
    private getContainerNodeId: (node: TContainer) => string | null,
    private getLeafNodeId: (node: TLeaf) => string,
    private debugPrintFolderData?: (node: TContainer) => string,
    private debugPrintLeafData?: (node: TLeaf) => string
  ) {
    const rootId = this.getContainerNodeId(root.data);
    if (!rootId) {
      throw new Error('Tree root must have a valid container id.');
    }
    this.containerNodeMap.set(`container:${rootId}`, root);
    this.parentByNode.set(root, null);
  }

  private readonly containerNodeMap: Map<string, ContainerNode<TContainer, TLeaf>> = new Map();
  private readonly leafNodeMap: Map<string, LeafNode<TContainer, TLeaf>> = new Map();
  private readonly parentByNode: WeakMap<TreeNode<TContainer, TLeaf>, ContainerNode<TContainer, TLeaf> | null> =
    new WeakMap();

  // mutations
  insertNode(
    node: TreeNode<TContainer, TLeaf>,
    targetContainerNode: ContainerNode<TContainer, TLeaf> = this.root
  ): boolean {
    // To build a tree successfully, insert nodes one at a time in parent-before-child order.
    // The target container must already belong to this tree...
    if (this.isRootNode(node) || this.parentByNode.has(node)) return false;
    if (!this.parentByNode.has(targetContainerNode)) return false;
    // ...and inserted containers must be empty so we can be sure that every descendant is indexed and parented.
    if (this.isContainerNode(node)) {
      if (node.children.length > 0) return false;

      const containerId = this.getContainerNodeId(node.data);
      if (!containerId || this.containerNodeMap.has(`container:${containerId}`)) return false;
      targetContainerNode.children.push(node);
      this.parentByNode.set(node, targetContainerNode);
      this.containerNodeMap.set(`container:${containerId}`, node);
      return true;
    }
    if (this.isLeafNode(node)) {
      const leafId = this.getLeafNodeId(node.data);
      if (!leafId || this.leafNodeMap.has(`leaf:${leafId}`)) return false;
      targetContainerNode.children.push(node);
      this.parentByNode.set(node, targetContainerNode);
      this.leafNodeMap.set(`leaf:${leafId}`, node);
      return true;
    }
    return false;
  }

  deleteNode(node: TreeNode<TContainer, TLeaf>): boolean {
    if (this.isRootNode(node)) return false;
    if (!this.parentByNode.has(node)) return false;
    return this.postorderDfs(node, this.removeNodeFromParent, true);
  }

  moveNode(node: TreeNode<TContainer, TLeaf>, targetContainerNode: ContainerNode<TContainer, TLeaf>): boolean {
    const currentParent = this.getParent(node);
    if (!currentParent || currentParent === targetContainerNode) return false;
    if (!this.parentByNode.has(targetContainerNode)) return false;
    if (this.isContainerNode(node)) {
      if (node === targetContainerNode) return false;
      if (this.ancestorContainsDescendantContainer(node, targetContainerNode)) return false;
    }
    if (!this.detachNodeFromContainer(node, currentParent)) return false;
    targetContainerNode.children.push(node);
    this.parentByNode.set(node, targetContainerNode);
    return true;
  }

  // lookup
  findNodeById(id: string): TreeNode<TContainer, TLeaf> | null {
    return this.containerNodeMap.get(`container:${id}`) ?? this.leafNodeMap.get(`leaf:${id}`) ?? null;
  }

  findContainerNodeById(id: string): ContainerNode<TContainer, TLeaf> | null {
    return this.containerNodeMap.get(`container:${id}`) ?? null;
  }

  findLeafNodeById(id: string): LeafNode<TContainer, TLeaf> | null {
    return this.leafNodeMap.get(`leaf:${id}`) ?? null;
  }

  getParent(node: TreeNode<TContainer, TLeaf>): ContainerNode<TContainer, TLeaf> | null {
    return this.parentByNode.get(node) ?? null;
  }

  // state
  isLeafNode(node: TreeNode<TContainer, TLeaf>): node is LeafNode<TContainer, TLeaf> {
    return node.kind === 'leaf';
  }

  isContainerNode(node: TreeNode<TContainer, TLeaf>): node is ContainerNode<TContainer, TLeaf> {
    return node.kind === 'container';
  }

  isRootNode(node: TreeNode<TContainer, TLeaf>): boolean {
    return node === this.root;
  }

  hasChildren(node: TreeNode<TContainer, TLeaf>) {
    if (
      (this.isContainerNode(node) || this.isRootNode(node)) &&
      (node as ContainerNode<TContainer, TLeaf>).children.length
    ) {
      return true;
    }
    return false;
  }

  getPath(node: TreeNode<TContainer, TLeaf>): TreeNode<TContainer, TLeaf>[] {
    const path: TreeNode<TContainer, TLeaf>[] = [];
    let cur: TreeNode<TContainer, TLeaf> | null = node;
    while (cur) {
      path.push(cur);
      if (this.isRootNode(cur)) break;
      cur = this.getParent(cur) ?? null;
    }
    return path.reverse();
  }

  getDepth(node: TreeNode<TContainer, TLeaf>): number | null {
    if (!this.parentByNode.has(node)) return null;
    return this.getPath(node).length - 1;
  }

  debugPrintPrettyString(
    getLabel: (node: TreeNode<TContainer, TLeaf>) => string = this.defaultDebugPrintCallback
  ): string {
    const lines: string[] = [];
    const dfs = (node: TreeNode<TContainer, TLeaf>, prefix: string, isLast: boolean) => {
      let connector = '';
      if (!this.isRootNode(node)) {
        if (isLast) {
          connector = '└── ';
        } else {
          connector = '├── ';
        }
      }
      lines.push(`${prefix}${connector}${getLabel(node)}`);
      if (this.isContainerNode(node)) {
        const children = node.children;
        const lastIndex = children.length - 1;
        children.forEach((child, index) => {
          const childIsLast = index === lastIndex;
          let nextPrefix = prefix;
          if (!this.isRootNode(node)) {
            if (isLast) {
              nextPrefix += '    ';
            } else {
              nextPrefix += '│   ';
            }
          }
          dfs(child, nextPrefix, childIsLast);
        });
      }
    };
    dfs(this.root, '', true);
    return lines.join('\n');
  }

  projectContainerSubtreeFrom<TView>(
    startNode: ContainerNode<TContainer, TLeaf>,
    projector: (
      node: ContainerNode<TContainer, TLeaf>,
      projectedChildren: TView[],
      depth: number,
      parentFolderId: string | null
    ) => TView
  ): TView {
    const dfs = (node: ContainerNode<TContainer, TLeaf>, depth: number): TView => {
      const projectedChildren: TView[] = [];
      for (const child of node.children) {
        if (!this.isContainerNode(child)) continue;
        projectedChildren.push(dfs(child, depth + 1));
      }
      const parent = this.getParent(node);
      const parentFolderId = parent ? this.getContainerNodeId(parent.data) : null;
      return projector(node, projectedChildren, depth, parentFolderId);
    };
    return dfs(startNode, 0);
  }

  private defaultDebugPrintCallback = (node: TreeNode<TContainer, TLeaf>): string => {
    if (this.isContainerNode(node)) {
      return `[C] ${this.debugPrintFolderData?.(node.data) ?? 'NONE'}`;
    }
    if (this.isLeafNode(node)) {
      return `[L] ${this.debugPrintLeafData?.(node.data) ?? 'NONE'}`;
    }
    return '[?] UNKNOWN';
  };

  private postorderDfs<TArgs extends unknown[]>(
    node: TreeNode<TContainer, TLeaf>,
    nodeHandler: (node: TreeNode<TContainer, TLeaf>, ...args: TArgs) => boolean,
    ...handlerArgs: TArgs
  ): boolean {
    if (this.isContainerNode(node)) {
      for (const child of [...node.children]) {
        if (!this.postorderDfs(child, nodeHandler, ...handlerArgs)) return false;
      }
    }
    return nodeHandler(node, ...handlerArgs);
  }

  public ancestorContainsDescendantContainer(
    ancestor: ContainerNode<TContainer, TLeaf>,
    descendant: ContainerNode<TContainer, TLeaf>
  ): boolean {
    for (const child of ancestor.children) {
      if (!this.isContainerNode(child)) continue;
      if (child === descendant) return true;
      if (this.ancestorContainsDescendantContainer(child, descendant)) return true;
    }
    return false;
  }

  public ancestorContainsDescendantLeaf(
    ancestor: ContainerNode<TContainer, TLeaf>,
    descendant: LeafNode<TContainer, TLeaf>
  ): boolean {
    for (const child of ancestor.children) {
      if (child === descendant) return true;
      if (!this.isContainerNode(child)) continue;
      if (this.ancestorContainsDescendantLeaf(child, descendant)) return true;
    }
    return false;
  }

  private removeNodeFromParent = (node: TreeNode<TContainer, TLeaf>, deleteNode: boolean = false): boolean => {
    if (this.isRootNode(node)) return false;
    const parentContainerNode = this.getParent(node);
    if (!parentContainerNode) return false;
    if (!this.detachNodeFromContainer(node, parentContainerNode)) return false;
    if (deleteNode) {
      return this.removeNodeFromIndexes(node);
    }
    return true;
  };

  private detachNodeFromContainer(
    node: TreeNode<TContainer, TLeaf>,
    containerNode: ContainerNode<TContainer, TLeaf>
  ): boolean {
    if (this.isRootNode(node)) return false;
    if (!this.detachChild(node, containerNode)) return false;
    this.parentByNode.delete(node);
    return true;
  }

  private removeNodeFromIndexes(node: TreeNode<TContainer, TLeaf>): boolean {
    if (this.isContainerNode(node)) {
      const containerNodeId = this.getContainerNodeId(node.data);
      if (containerNodeId) {
        this.containerNodeMap.delete(`container:${containerNodeId}`);
        return true;
      }
    }
    if (this.isLeafNode(node)) {
      const leafNodeId = this.getLeafNodeId(node.data);
      if (leafNodeId) {
        this.leafNodeMap.delete(`leaf:${leafNodeId}`);
        return true;
      }
    }
    return false;
  }

  private detachChild(node: TreeNode<TContainer, TLeaf>, parent: ContainerNode<TContainer, TLeaf>): boolean {
    const index = parent.children.indexOf(node);
    if (index === -1) return false;
    parent.children.splice(index, 1);
    return true;
  }
}
